import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { showToast } from '@Sage/hoi-poi-ui';
import { ExternalEmbedModal, getAllowedOriginFromSrc } from '@Sage/iframe-embed';

import {
    getEmbedSandboxUnavailableText,
    probeEmbedSandboxUrl,
} from 'components/DevPopUp/embedSandbox';
import { IS_PRODUCTION } from 'constants/Environment';
import Context from 'managers/Context';
import { ExternalService } from 'services';
import { getActiveCrud } from 'utils/crud';
import { handleEmbedCloseOutcome } from 'utils/embedCloseOutcomes';
import {
    createEmbedSessionIds,
    getEmbedEntityId,
    getRuntimeAuthToken,
    normalizeEmbedInitContext,
    refreshRuntimeAuthToken,
    resolveHostIframeSrc,
} from 'utils/embedHost';
import {
    getExternalCrudConfig,
    hasExternalCrud,
    resolveEmbedCrudMode,
    shouldSkipExternalFormsInit,
} from 'utils/externalCrud';
import { getEntityFromString } from 'utils/getEntityFromString';
import { getLiteral } from 'utils/getLiteral';

const IFRAME_ALLOW = IS_PRODUCTION ? undefined : 'local-network-access';
const MAX_IFRAME_HEIGHT = 820;
const EMBED_TOAST_DEFAULTS = {
    autoClose: 6000,
    position: 'bottomRight',
    transition: 'slide',
    useDefaultCloseButton: true,
};

const EmbeddedEntityCrud = memo(
    ({
        entityKey: entityKeyProp,
        isOpen: isOpenProp,
        mode: modeProp,
        onClose: onCloseProp,
        title: titleProp,
    }) => {
        const dispatch = useDispatch();
        const store = useStore();

        // --- State
        const { activeCrud, entityKey, isOpen, token, userData } = useSelector((state) => {
            const currentActiveCrud = getActiveCrud(state);
            const resolvedEntityKey = entityKeyProp || currentActiveCrud?.entity;

            return {
                activeCrud: currentActiveCrud,
                entityKey: resolvedEntityKey,
                isOpen:
                    isOpenProp ??
                    (state.entityCrud?.isOpen && currentActiveCrud?.entity === resolvedEntityKey),
                token: state.config?.token,
                userData: state.config?.userData,
            };
        });

        const [embedInitContext, setEmbedInitContext] = useState(null);
        const [initLoadState, setInitLoadState] = useState('idle');
        const [sandboxReachable, setSandboxReachable] = useState(null);

        // --- Derived
        const entity = useMemo(() => getEntityFromString(entityKey), [entityKey]);
        const mode = modeProp || resolveEmbedCrudMode(activeCrud);
        const entityId = getEmbedEntityId(activeCrud?.id);
        const externalCrudConfig = useMemo(() => {
            return getExternalCrudConfig(entityKey, userData);
        }, [entityKey, userData]);
        const isSandboxEmbed = shouldSkipExternalFormsInit(externalCrudConfig);
        const sandboxUrl = isSandboxEmbed ? externalCrudConfig.url : null;
        const sandboxInitContext = useMemo(() => {
            if (!isSandboxEmbed || !entityKey) return null;

            return normalizeEmbedInitContext(entityKey, {
                sandbox: true,
                url: sandboxUrl,
            });
        }, [entityKey, isSandboxEmbed, sandboxUrl]);
        const resolvedInitContext = isSandboxEmbed ? sandboxInitContext : embedInitContext;
        const isHostPreparing = Boolean(isSandboxEmbed && isOpen && sandboxReachable === null);
        const sandboxUnreachable = Boolean(isSandboxEmbed && sandboxReachable === false);
        const embedSessionIds = useMemo(() => {
            return createEmbedSessionIds(resolvedInitContext);
        }, [resolvedInitContext]);
        const failedToInit = !isSandboxEmbed && initLoadState === 'error';
        const iframeSrc =
            isHostPreparing || sandboxUnreachable
                ? null
                : resolveHostIframeSrc({
                      entityConfig: externalCrudConfig,
                      entityKey,
                      failedToInit,
                      initContext: resolvedInitContext,
                  });
        const iframeTitle = titleProp || 'External form';

        // --- Handlers
        const getAuthContext = useCallback(
            async ({ request } = {}) => {
                const nextToken =
                    request?.reason === 'unauthorized'
                        ? await refreshRuntimeAuthToken(token)
                        : getRuntimeAuthToken(token);

                return {
                    token: nextToken,
                };
            },
            [token],
        );

        const onEmbedClose = useCallback(
            (payload = {}) => {
                if (!entity) {
                    if (onCloseProp) {
                        onCloseProp();
                    }
                    return;
                }

                const manager = Context.entityManager.getEntitiesManager(entity);

                handleEmbedCloseOutcome({
                    dispatch,
                    entity,
                    entityCrud: activeCrud,
                    getState: store.getState,
                    manager,
                    onCloseProp,
                    payload,
                });
            },
            [activeCrud, dispatch, entity, onCloseProp, store],
        );

        const onEmbedNotify = useCallback((payload = {}) => {
            const message = payload?.text ?? payload?.message;
            if (!message) return;

            showToast({
                ...EMBED_TOAST_DEFAULTS,
                text: message,
                title: payload?.title,
                type: payload?.type ?? 'info',
            });
        }, []);

        const onInitError = useCallback(() => {
            if (isSandboxEmbed) return;

            setInitLoadState('error');
            setEmbedInitContext(null);
        }, [isSandboxEmbed]);

        // --- Effects
        useEffect(() => {
            if (!isSandboxEmbed || !isOpen || !sandboxUrl) {
                setSandboxReachable(null);
                return undefined;
            }

            let isActive = true;
            setSandboxReachable(null);

            void probeEmbedSandboxUrl(sandboxUrl).then((ok) => {
                if (isActive) setSandboxReachable(ok);
            });

            return () => {
                isActive = false;
            };
        }, [isOpen, isSandboxEmbed, sandboxUrl]);

        useEffect(() => {
            if (isSandboxEmbed) {
                return undefined;
            }

            if (!isOpen || !externalCrudConfig || !entityKey) {
                setEmbedInitContext(null);
                setInitLoadState('idle');
                return undefined;
            }

            if (!hasExternalCrud(entityKey, userData, mode)) {
                setEmbedInitContext(null);
                setInitLoadState('idle');
                return undefined;
            }

            let isActive = true;
            setEmbedInitContext(null);
            setInitLoadState('loading');

            const loadEmbedInitContext = async () => {
                try {
                    const initialPayload = await ExternalService.initIframe({
                        ...externalCrudConfig,
                        entityType: entityKey,
                        ...(entityId != null ? { entityId } : {}),
                    });

                    if (isActive) {
                        setEmbedInitContext(normalizeEmbedInitContext(entityKey, initialPayload));
                        setInitLoadState('ready');
                    }
                } catch (error) {
                    if (isActive) {
                        setEmbedInitContext(null);
                        setInitLoadState('error');
                    }
                }
            };

            void loadEmbedInitContext();

            return () => {
                isActive = false;
            };
        }, [entityId, entityKey, externalCrudConfig, isOpen, isSandboxEmbed, mode, userData]);

        const shouldRenderModal =
            isOpen &&
            !!externalCrudConfig &&
            hasExternalCrud(entityKey, userData, mode) &&
            (isSandboxEmbed || initLoadState === 'ready' || initLoadState === 'error');

        // --- Render props
        const modalProps = useMemo(
            () => ({
                allow: IFRAME_ALLOW,
                allowedOrigin: iframeSrc ? getAllowedOriginFromSrc(iframeSrc) : null,
                chromeCloseCancelText: getLiteral('action_cancel'),
                chromeCloseConfirmActionText: getLiteral('action_discard_changes'),
                chromeCloseConfirmTitle: getLiteral('label_discard_unsaved_changes'),
                confirmOnChromeClose: true,
                getAuthContext,
                iframeSrc,
                iframeSizing: 'fill',
                iframeTitle,
                initContext: resolvedInitContext,
                initExtras: {
                    supportsNotifications: false,
                    ...embedSessionIds,
                },
                isHostPreparing,
                isOpen: shouldRenderModal,
                loadErrorText: sandboxUnreachable
                    ? getEmbedSandboxUnavailableText(sandboxUrl)
                    : getLiteral('error_connection'),
                maxIframeHeight: MAX_IFRAME_HEIGHT,
                mode,
                onClose: onEmbedClose,
                onEmbedNotify,
                onInitError,
                overlayClassName: 'fm-entity-modal__overlay',
                confirmOverlayClassName: 'fm-entity-modal__overlay-cancel',
                renderStateFooter: false,
                size: 'huge',
                title: titleProp,
            }),
            [
                embedSessionIds,
                getAuthContext,
                iframeSrc,
                iframeTitle,
                isHostPreparing,
                mode,
                onEmbedClose,
                onEmbedNotify,
                onInitError,
                resolvedInitContext,
                sandboxUnreachable,
                sandboxUrl,
                shouldRenderModal,
                titleProp,
            ],
        );

        return <ExternalEmbedModal {...modalProps} />;
    },
);

EmbeddedEntityCrud.displayName = 'EmbeddedEntityCrud';

export default EmbeddedEntityCrud;
